import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiHeart, FiInfo, FiSettings, FiArrowRight } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';
import WidgetHeader from './WidgetHeader';
import { useAuth } from '../../contexts/AuthContext';
import { usePulseData } from '../../hooks/usePulseData';

const DailyHeartbeatCard: React.FC = () => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePulseData();
  const [showTooltip, setShowTooltip] = useState(false);

  const isAdmin = user?.is_superuser === true;

  const base = `rounded-lg border border-l-4 p-4 ${darkMode ? 'bg-indigo-950/30 border-indigo-900/20 border-l-indigo-500' : 'bg-indigo-50/40 border-indigo-100 border-l-indigo-500'}`;

  // Loading state
  if (isLoading) {
    return (
      <div className={base}>
        <WidgetHeader hero icon={<FiHeart className="w-4 h-4 text-indigo-400" />} title="Daily Briefing" accentColor="indigo" />
        <div className="animate-pulse space-y-2">
          <div className={`h-3 w-full rounded ${darkMode ? 'bg-gray-700' : 'bg-indigo-100'}`} />
          <div className={`h-3 w-4/5 rounded ${darkMode ? 'bg-gray-700' : 'bg-indigo-100'}`} />
        </div>
      </div>
    );
  }

  const items = data?.items ?? [];
  const latest = items.length > 0 ? items[0] : null;

  // Empty state — no pulse yet
  if (!latest || latest.zero_data) {
    return (
      <div className={base}>
        <WidgetHeader hero icon={<FiHeart className="w-4 h-4 text-indigo-400" />} title="Daily Briefing" accentColor="indigo" />
        <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          No briefing yet — first pulse generates tomorrow at 6 AM.
        </p>
      </div>
    );
  }

  // Stale / failed state
  const generatedAt = latest.generated_at ? new Date(latest.generated_at) : null;
  const hoursOld = generatedAt ? (Date.now() - generatedAt.getTime()) / 3600000 : 0;
  const isStale = hoursOld > 30;

  // Metadata line
  const tokenCount = latest.token_usage?.total_tokens;
  const tier = latest.tier ?? 'standard';
  const timeStr = generatedAt
    ? generatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';
  const dateStr = generatedAt
    ? generatedAt.toLocaleDateString([], { month: 'short', day: 'numeric' })
    : '';

  return (
    <div className={`${base} relative`}>
      {/* WidgetHeader with composite breathing-dot icon + actions */}
      <WidgetHeader
        hero
        icon={
          <span className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full bg-indigo-400 shrink-0"
              style={{ animation: 'breathe 2s ease-in-out infinite' }}
            />
            <FiHeart className="w-4 h-4 text-indigo-400" />
          </span>
        }
        title={latest.name ?? 'Daily Briefing'}
        subtitle={tier}
        accentColor="indigo"
        actions={
          <span className="flex items-center gap-1">
            {/* Info tooltip */}
            <div className="relative">
              <button
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                className={`p-1 rounded transition-colors ${darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                aria-label="About Daily Briefing"
              >
                <FiInfo className="w-3.5 h-3.5" />
              </button>
              {showTooltip && (
                <div className={`absolute right-0 top-6 z-20 w-64 p-2 rounded-lg text-xs shadow-lg ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                  System-generated daily briefing. An AI persona reflects on platform activity and
                  narrates a summary. Admins can configure the narrator, tier, and schedule via Admin Chat.
                </div>
              )}
            </div>
            {/* Admin cog */}
            {isAdmin && (
              <button
                onClick={() =>
                  navigate(`/admin/chat?prompt=${encodeURIComponent('Show the current heartbeat pulse configuration using list_pulses. Then explain how to change tier (brief/standard/detailed), date_range_days, enabled status, narrator persona, and model using update_pulse_config.')}`)
                }
                className={`p-1 rounded transition-colors ${darkMode ? 'text-gray-500 hover:text-indigo-400' : 'text-gray-400 hover:text-indigo-600'}`}
                aria-label="Configure pulse in Admin Chat"
              >
                <FiSettings className="w-3.5 h-3.5" />
              </button>
            )}
          </span>
        }
      />

      {/* Stale / failed warning */}
      {isStale && (
        <p className="text-xs text-amber-400 mb-2">
          Last briefing was {Math.floor(hoursOld)}h ago
          {isAdmin ? ' — admin can re-run via Admin Chat' : ''}.
        </p>
      )}

      {/* Content preview */}
      <p className={`text-xs leading-relaxed line-clamp-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        {latest.content_preview ?? ''}
      </p>

      {/* Metadata + actions row */}
      <div className="flex items-center justify-between gap-2 mt-3">
        <span className={`text-[10px] ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>
          {dateStr} {timeStr}
          {tier && (
            <span className={`ml-1 px-1 rounded ${darkMode ? 'bg-indigo-900/40 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
              {tier}
            </span>
          )}
          {tokenCount != null && ` · ${tokenCount.toLocaleString()} tokens`}
        </span>

        <div className="flex items-center gap-3">
          {/* "Try it" link — editors + admins (show to all authenticated users, API enforces) */}
          {user && (
            <button
              onClick={() =>
                navigate('/work/chat?prompt=Dry+run+a+heartbeat+for+the+last+3+days')
              }
              className={`text-[10px] transition-colors ${darkMode ? 'text-indigo-500 hover:text-indigo-400' : 'text-indigo-400 hover:text-indigo-600'}`}
            >
              Explore → dry run
            </button>
          )}

          {/* View full briefing */}
          <button
            onClick={() => navigate(`/heartbeat?expand=${latest.artifact_id}`)}
            className={`flex items-center gap-1 text-xs transition-colors ${darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`}
          >
            View full briefing <FiArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>

    </div>
  );
};

export default DailyHeartbeatCard;
